import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { getUserVideos, getVideoLink } from "../../api/lib/user";

/**
 * The videos a parent has made, inside the user detail modal.
 *
 * Links are minted one at a time when the admin asks for one, not fetched with
 * the list. Two reasons: signing is real work per video, and a link plays
 * without any login for as long as it lives — so creating one for every row
 * would hand out a pile of shareable links nobody requested.
 */

const formatDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? ""
    : date.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
};

const UserVideosList = ({ userId }) => {
  const [videos, setVideos] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Which video is currently being minted, so only that button shows a spinner.
  const [pendingId, setPendingId] = useState(null);
  // Links kept per video for as long as the modal is open, so clicking twice
  // does not sign twice.
  const [links, setLinks] = useState({});

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setError(null);
    try {
      const response = await getUserVideos(userId);
      setVideos(response?.data?.data ?? []);
      setMeta(response?.data?.meta ?? null);
    } catch {
      setError("Could not load this user's videos.");
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    load();
  }, [load]);

  // Reset when switching users, or the previous user's links would linger.
  useEffect(() => {
    setLinks({});
  }, [userId]);

  /**
   * Copies the link, falling back to "it is on screen" when it cannot.
   *
   * Never throws. The clipboard needs a secure context and can also reject on
   * permission or focus rules — but by this point the link exists and is
   * rendered, so a copy failure must not surface as "could not create a link".
   */
  const copyToClipboard = async (url, alreadyHad) => {
    try {
      if (!navigator.clipboard?.writeText) throw new Error("unavailable");
      await navigator.clipboard.writeText(url);
      toast.success(alreadyHad ? "Link copied again." : "Link copied. It works for 24 hours.");
    } catch {
      toast.info("Link ready below, copy it from there. It works for 24 hours.");
    }
  };

  const handleGetLink = async (videoId) => {
    // Copying again reuses the link already minted for this video. Calling the
    // API a second time would create another shareable url for the same video,
    // which is exactly what minting on demand is meant to avoid.
    const existing = links[videoId];
    if (existing) {
      await copyToClipboard(existing, true);
      return;
    }

    setPendingId(videoId);
    try {
      const response = await getVideoLink(videoId);
      const url = response?.data?.data?.url;
      if (!url) throw new Error("no url");
      setLinks((current) => ({ ...current, [videoId]: url }));
      await copyToClipboard(url, false);
    } catch {
      toast.error("Could not create a link for that video.");
    } finally {
      setPendingId(null);
    }
  };

  if (loading) return <p className="user-videos-empty">Loading videos…</p>;
  if (error) return <p className="user-videos-empty">{error}</p>;
  if (videos.length === 0) {
    return <p className="user-videos-empty">This user has not made any videos yet.</p>;
  }

  return (
    <>
      {/* Never let a cap pass silently: a truncated list that looks complete
          is worse than one that says so. */}
      {meta?.truncated && (
        <p className="user-videos-note">
          Showing the newest {meta.returned} of {meta.total} videos.
        </p>
      )}
      <div className="user-videos-list">
      {videos.map((video) => (
        <div className="user-video-row" key={video._id}>
          <div className="user-video-meta">
            <span className="user-video-title">{video.title || "Untitled"}</span>
            <span className="user-video-sub">
              {[video.template, formatDate(video.createdAt)].filter(Boolean).join(" · ")}
            </span>
            {links[video._id] && (
              <input
                className="user-video-link"
                value={links[video._id]}
                readOnly
                onFocus={(event) => event.target.select()}
              />
            )}
          </div>

          {/* A record whose upload never finished has no file behind it, so
              offering a button would only produce a dead link. */}
          {video.hasVideo ? (
            <button
              type="button"
              className="user-video-btn"
              onClick={() => handleGetLink(video._id)}
              disabled={pendingId === video._id}
            >
              {pendingId === video._id
                ? "Creating…"
                : links[video._id]
                ? "Copy again"
                : "Get link"}
            </button>
          ) : (
            <span className="user-video-missing" title="This video did not finish uploading">
              No file
            </span>
          )}
        </div>
      ))}
      </div>
    </>
  );
};

export default UserVideosList;
